import { Autocomplete, Box, FormControl, FormHelperText, InputLabel, TextField, ThemeProvider, createTheme } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import Functions from '../../Helpers/Functions';
import cities from '../../Assets/file/code_ville.json'
import { useTailwindBrand } from '../shared';
import URSSAFParentRepository from '../../Repositories/URSSAFParentRepository';

interface City {
    label: string,
    value: string
}

interface SelectCityProps {
    zipCode?: string
    callBack: (value: string) => void
    value?: string
    error?: string
    required?: boolean
    label?: string
}

const SelectCity = (props: SelectCityProps) => {
    const { zipCode, callBack, value: propValue } = props
    const [value, setValue] = useState<City | null>(null)
    const [options, setOptions] = useState<City[]>(cities)
    const isFirstRun = useRef(true)
    const valueRef = useRef<City | null>(null)
    const callBackRef = useRef(callBack)

    useEffect(() => {
        callBackRef.current = callBack
    })

    const updateValue = (newVal: City | null) => {
        valueRef.current = newVal
        setValue(newVal)
    }

    useEffect(() => {
        if (zipCode && zipCode !== '') {
            const firstRun = isFirstRun.current
            isFirstRun.current = false

            if (!firstRun) {
                updateValue(null)
                callBackRef.current("")
            }

            const processOptions = (newOptions: City[]) => {
                setOptions(newOptions)
                if (newOptions.length === 1) {
                    updateValue(newOptions[0])
                    callBackRef.current(newOptions[0].label)
                } else if (firstRun) {
                    const current = valueRef.current
                    if (current && !newOptions.some(opt => opt.label === current.label)) {
                        updateValue(null)
                        callBackRef.current("")
                    }
                }
            }

            URSSAFParentRepository.findCityByZipCode(zipCode).then((res) => {
                if (res && res.length > 0) {
                    processOptions(res.map((commune: { label: string }) => ({ label: commune.label, value: zipCode })))
                } else {
                    processOptions(Functions.getCities(zipCode))
                }
            }).catch(() => {
                processOptions(Functions.getCities(zipCode))
            })
        } else {
            isFirstRun.current = false
            updateValue(null)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zipCode])

    useEffect(() => {
        if (propValue) {
            const city = Functions.getcity(propValue)
            updateValue(city)
            callBackRef.current(propValue)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const { classes, brandConfig } = useTailwindBrand()

    const theme = createTheme({
        components: {
            MuiAutocomplete: {
                styleOverrides: {
                    option: { fontFamily: brandConfig.fontFamily },
                },
            },
        },
    });

    return (
        <div>
            <ThemeProvider theme={theme}>
                <FormControl variant="filled" fullWidth>
                    {/* <InputLabel
                        required={props.required}
                        className={`${classes.fontFamily} ${props.error ? classes.error : classes.text} font-semibold`}
                    >
                        {props.label ?? 'Ville de naissance'}
                    </InputLabel> */}
                    <Autocomplete
                        disabled={!zipCode}
                        value={value}
                        defaultValue={propValue ? Functions.getcity(propValue) : null}
                        onChange={(_, newValue) => {
                            if (newValue) {
                                updateValue(newValue);
                                callBack(newValue.label)
                            } else {
                                updateValue(null)
                                callBack("")
                            }
                        }}
                        id="city-select"
                        options={options}
                        autoHighlight
                        getOptionLabel={(option) => option.label}
                        renderOption={(optProps, option) => (
                            <Box component="li" {...optProps}>
                                {option.label}
                            </Box>
                        )}
                        className={`${classes.fontFamily} ${props.error ? classes.errorBorder + ' border-l-4' : 'border-gray-400'} bg-white rounded-md border-2 `}
                        sx={{
                            fontFamily: brandConfig.fontFamily,
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="filled"
                                placeholder="Sélectionnez votre ville"
                                error={!!props.error}
                                className={`${classes.fontFamily} ${props.error ? classes.errorBorder + ' border-l-4' : 'border-gray-400'} bg-white rounded-md border`}
                                inputProps={{
                                    ...params.inputProps,
                                    autoComplete: 'new-password',
                                }}
                                sx={{
                                    '& .MuiFilledInput-root': {
                                        fontFamily: brandConfig.fontFamily,
                                        backgroundColor: 'transparent',
                                    },
                                    '& .MuiFilledInput-root::before': { border: 'none' },
                                    '& .MuiFilledInput-root::after': { border: 'none' },
                                    '& .MuiFilledInput-root:hover::before': { border: 'none' },
                                    '& input': { fontFamily: brandConfig.fontFamily },
                                }}
                                label={<label
                                    className={`${classes.fontFamily} ${props.error ? classes.error : classes.text} font-semibold`}>
                                    {props.label ?? 'Ville de naissance'}<span className={props.required ? 'text-red-700' : ''}>{" "}*</span>
                                </label>}
                            />
                        )}
                    />
                </FormControl>
            </ThemeProvider>
            {props.error && (
                <FormHelperText className={`${classes.fontFamily} ${classes.error} italic font-semibold`}>
                    {props.error}
                </FormHelperText>
            )}
        </div>
    );
};

export default SelectCity;
